import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookHeart, Settings, LogOut, User, Shield, FileText, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

interface AppHeaderProps {
  onOpenProfile?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  onOpenProfile,
}) => {
  const navigate = useNavigate();
  const { user, isAdmin, isDevMode, toggleDevMode, signOut, userProfile } = useAuthStore();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
    navigate('/');
  };

  const userInitials = userProfile?.firstName
    ? `${userProfile.firstName.charAt(0)}${userProfile.lastName?.charAt(0) || ''}`
    : user?.email?.charAt(0).toUpperCase() || 'U';

  return (
    <header className="bg-white/95 backdrop-blur-md border-b border-stone-200 sticky top-0 z-sticky">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center space-x-2 flex-shrink-0">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-rose-400 rounded-full flex items-center justify-center text-white">
            <BookHeart className="h-5 w-5 sm:h-6 sm:w-6" />
          </div>
          <span className="text-lg sm:text-2xl font-bold text-stone-800 tracking-tight">
            Ethernal
          </span>
        </Link>

        {/* Family pill removed (PRO-344 iter 2) — the dashboard "Estás viendo"
            chip is the single switching surface; sidebar/bottom-nav cover other
            pages. */}

        {/* Admin view toggle — admin only */}
        {isAdmin && (
          <button
            onClick={toggleDevMode}
            className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
              isDevMode
                ? 'bg-amber-100 text-amber-700 border border-amber-300'
                : 'bg-stone-100 text-stone-400 border border-stone-200'
            }`}
            title={isDevMode ? 'Admin view ON — click to see as user' : 'Viewing as user — click for admin view'}
          >
            <div className={`w-2 h-2 rounded-full ${isDevMode ? 'bg-amber-500' : 'bg-stone-300'}`} />
            {isDevMode ? 'Admin view' : 'User view'}
          </button>
        )}

        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-stone-100 to-stone-200 border-2 border-white shadow-sm flex items-center justify-center text-stone-600 hover:shadow-md transition-all"
          >
            <span className="text-sm font-semibold">{userInitials}</span>
          </button>

          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-sticky"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-stone-200 py-2 z-modal animate-fade-in">
                <div className="px-4 py-2 border-b border-stone-100">
                  <p className="font-medium text-stone-900 truncate">
                    {userProfile?.firstName
                      ? `${userProfile.firstName} ${userProfile.lastName || ''}`
                      : user?.email}
                  </p>
                  <p className="text-xs text-stone-500 truncate">
                    {user?.email}
                  </p>
                </div>

                <div className="py-1">
                  {onOpenProfile && (
                    <button
                      onClick={() => { setShowUserMenu(false); onOpenProfile(); }}
                      className="flex items-center gap-3 px-4 py-2.5 text-stone-700 hover:bg-stone-50 transition-colors w-full"
                    >
                      <User className="h-4 w-4" />
                      <span className="text-sm">Perfil</span>
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => { toggleDevMode(); setShowUserMenu(false); }}
                      className="relative z-modal flex items-center justify-between gap-3 px-4 py-2.5 text-stone-700 hover:bg-stone-50 transition-colors min-h-0 w-full"
                    >
                      <span className="flex items-center gap-3">
                        {isDevMode ? (
                          <ToggleRight className="h-4 w-4 text-amber-600" />
                        ) : (
                          <ToggleLeft className="h-4 w-4 text-stone-400" />
                        )}
                        <span className="text-sm">Vista admin</span>
                      </span>
                      <span className={`text-xs font-semibold ${isDevMode ? 'text-amber-600' : 'text-stone-400'}`}>
                        {isDevMode ? 'ON' : 'OFF'}
                      </span>
                    </button>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setShowUserMenu(false)}
                      className="relative z-modal flex items-center gap-3 px-4 py-2.5 text-stone-700 hover:bg-stone-50 transition-colors min-h-0"
                    >
                      <Settings className="h-4 w-4" />
                      <span className="text-sm">Panel de Admin</span>
                    </Link>
                  )}
                  <Link
                    to="/privacidad"
                    onClick={() => setShowUserMenu(false)}
                    className="relative z-modal flex items-center gap-3 px-4 py-2.5 text-stone-700 hover:bg-stone-50 transition-colors min-h-0"
                  >
                    <Shield className="h-4 w-4" />
                    <span className="text-sm">Privacidad</span>
                  </Link>
                  <Link
                    to="/terminos"
                    onClick={() => setShowUserMenu(false)}
                    className="relative z-modal flex items-center gap-3 px-4 py-2.5 text-stone-700 hover:bg-stone-50 transition-colors min-h-0"
                  >
                    <FileText className="h-4 w-4" />
                    <span className="text-sm">Términos</span>
                  </Link>
                </div>

                <div className="border-t border-stone-100 pt-1">
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors w-full"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="text-sm">Cerrar sesión</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
